export const SPRITEFUSION_SPRITE = "spritefusion_tiles";
/** Static sheet under public/map — drop-in replace without rebuild of imports. */
export const SPRITEFUSION_SPRITESHEET_URL = "map/spritesheet.png";
export const SPRITEFUSION_MAP_URL = "map/map.lua";

const LAYER_Z = {
    room: -30,
    streetstuff: -20,
    clue: 9709.25,
    clues: 9709.25,
    tunnel: 9709.35,
    tunnels: 9709.35,
    decor: 9505,
    tabletops: 0,
    doors: 9510,
    ads: 9520,
    light: 9709.5,
    lights: 9709.5,
};

let cachedMap = null;
let loadPromise = null;

function parseNumbers(listSource) {
    return [...listSource.matchAll(/\d+/g)].map((match) => Number(match[0]));
}

function normalizeName(name) {
    return name.toLowerCase().replace(/\s+/g, "");
}

function parseLastNumber(source, key) {
    const matches = [...source.matchAll(new RegExp(`\\n  ${key} = (\\d+),`, "g"))];
    if (matches.length === 0) return 0;
    return Number(matches[matches.length - 1][1]);
}

function parseMap(source) {
    const layers = [];
    const layerRegex = /\{\s*data = \{([\s\S]*?)\},\s*height = (\d+),\s*id = \d+,\s*name = "([^"]+)"[\s\S]*?visible = (true|false),\s*width = (\d+),[\s\S]*?\n    \},/g;

    let match;
    while ((match = layerRegex.exec(source))) {
        const data = parseNumbers(match[1]);
        layers.push({
            name: match[3],
            key: normalizeName(match[3]),
            visible: match[4] === "true",
            width: Number(match[5]),
            height: Number(match[2]),
            data,
        });
    }

    const tilesetMatch = source.match(/columns = (\d+),[\s\S]*?firstgid = (\d+),[\s\S]*?tilecount = (\d+),[\s\S]*?tileheight = (\d+),[\s\S]*?tilewidth = (\d+),/);

    return {
        width: parseLastNumber(source, "width"),
        height: parseLastNumber(source, "height"),
        tileWidth: parseLastNumber(source, "tilewidth"),
        tileHeight: parseLastNumber(source, "tileheight"),
        layers,
        tileset: {
            columns: tilesetMatch ? Number(tilesetMatch[1]) : 1,
            firstgid: tilesetMatch ? Number(tilesetMatch[2]) : 1,
            tilecount: tilesetMatch ? Number(tilesetMatch[3]) : 0,
            tileWidth: tilesetMatch ? Number(tilesetMatch[5]) : 16,
            tileHeight: tilesetMatch ? Number(tilesetMatch[4]) : 16,
        },
    };
}

/**
 * Fetch + parse public/map/map.lua. Call before createLevel().
 */
export async function loadSpriteFusionMap() {
    if (cachedMap) return cachedMap;
    if (!loadPromise) {
        loadPromise = fetch(SPRITEFUSION_MAP_URL)
            .then((res) => {
                if (!res.ok) {
                    throw new Error(`Failed to load ${SPRITEFUSION_MAP_URL} (${res.status})`);
                }
                return res.text();
            })
            .then((source) => {
                cachedMap = parseMap(source);
                return cachedMap;
            });
    }
    return loadPromise;
}

export function getSpriteFusionMap() {
    if (!cachedMap) {
        throw new Error("SpriteFusion map not loaded — await loadSpriteFusionMap() first");
    }
    return cachedMap;
}

export function getLayer(map, names) {
    const keys = names.map(normalizeName);
    return map.layers.find((layer) => keys.includes(layer.key)) || null;
}

export function getRenderableLayers(map) {
    // Collision / markers / fog regions — not drawn as map art
    const skip = new Set([
        "anchor",
        "anchors",
        "walkable",
        "checkpoints",
        "checkpoint",
        "spawn",
        "regions",
        "roomregions",
        "room_regions",
    ]);
    return map.layers.filter((layer) => (
        layer.visible
        && !skip.has(layer.key)
        && !layer.key.startsWith("anchor")
        && !layer.key.startsWith("marker")
    ));
}

export function getLayerZ(layerName) {
    return LAYER_Z[normalizeName(layerName)] ?? 0;
}

export function getTileGid(layer, x, y) {
    if (!layer || x < 0 || y < 0 || x >= layer.width || y >= layer.height) return 0;
    return layer.data[y * layer.width + x] || 0;
}

export function tileGidToFrame(map, gid) {
    return Math.max(0, gid - map.tileset.firstgid);
}

export function hasWalkableLayer(map) {
    return Boolean(getLayer(map, ["walkable"]));
}

export function buildCollisionRows(map) {
    const walkable = getLayer(map, ["walkable"]);

    return Array.from({ length: map.height }, (_, y) => {
        let row = "";
        for (let x = 0; x < map.width; x++) {
            const passable = walkable ? getTileGid(walkable, x, y) > 0 : false;
            row += passable ? "." : "#";
        }
        return row;
    });
}

export function getPassableCells(collisionRows) {
    const cells = [];
    for (let y = 0; y < collisionRows.length; y++) {
        for (let x = 0; x < collisionRows[y].length; x++) {
            if (collisionRows[y][x] === ".") {
                cells.push({ x, y });
            }
        }
    }
    return cells;
}

export function findNearestPassableCell(collisionRows, targetX, targetY) {
    const cells = getPassableCells(collisionRows);
    if (cells.length === 0) return { x: 1, y: 1 };

    let best = cells[0];
    let bestDist = Infinity;
    for (const cell of cells) {
        const dx = cell.x - targetX;
        const dy = cell.y - targetY;
        const dist = dx * dx + dy * dy;
        if (dist < bestDist) {
            best = cell;
            bestDist = dist;
        }
    }
    return best;
}

/**
 * Prefer a walkable cell that also has Room (floor) art — near the walkable centroid.
 */
export function findFloorSpawnCell(map, collisionRows) {
    const room = getLayer(map, ["room"]);
    const cells = getPassableCells(collisionRows);
    if (cells.length === 0) return { x: 1, y: 1 };

    const floorCells = room
        ? cells.filter((c) => getTileGid(room, c.x, c.y) > 0)
        : cells;
    const pool = floorCells.length > 0 ? floorCells : cells;

    const cx = Math.round(pool.reduce((s, c) => s + c.x, 0) / pool.length);
    const cy = Math.round(pool.reduce((s, c) => s + c.y, 0) / pool.length);

    // Prefer cells whose 3x3 neighborhood is also walkable (interior floor)
    let best = null;
    let bestScore = -Infinity;
    for (const cell of pool) {
        let neighbors = 0;
        for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
                const nx = cell.x + dx;
                const ny = cell.y + dy;
                if (ny < 0 || ny >= collisionRows.length) continue;
                if (nx < 0 || nx >= collisionRows[ny].length) continue;
                if (collisionRows[ny][nx] === ".") neighbors++;
            }
        }
        const dist = (cell.x - cx) ** 2 + (cell.y - cy) ** 2;
        const score = neighbors * 1000 - dist;
        if (score > bestScore) {
            bestScore = score;
            best = cell;
        }
    }

    return best || findNearestPassableCell(collisionRows, cx, cy);
}

/**
 * Flood-fill walkable cells into room bounding rects for distance fog.
 */
export function buildRoomRectsFromCollision(collisionRows) {
    const rows = collisionRows.length;
    const cols = rows > 0 ? collisionRows[0].length : 0;
    const visited = Array.from({ length: rows }, () => Array(cols).fill(false));
    const rooms = [];
    let nextId = 1;

    const passable = (x, y) =>
        y >= 0 && y < rows && x >= 0 && x < cols && collisionRows[y][x] === ".";

    for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
            if (!passable(x, y) || visited[y][x]) continue;

            let minX = x;
            let maxX = x;
            let minY = y;
            let maxY = y;
            const stack = [{ x, y }];
            visited[y][x] = true;

            while (stack.length > 0) {
                const cell = stack.pop();
                minX = Math.min(minX, cell.x);
                maxX = Math.max(maxX, cell.x);
                minY = Math.min(minY, cell.y);
                maxY = Math.max(maxY, cell.y);

                const neighbors = [
                    { x: cell.x + 1, y: cell.y },
                    { x: cell.x - 1, y: cell.y },
                    { x: cell.x, y: cell.y + 1 },
                    { x: cell.x, y: cell.y - 1 },
                ];
                for (const n of neighbors) {
                    if (!passable(n.x, n.y) || visited[n.y][n.x]) continue;
                    visited[n.y][n.x] = true;
                    stack.push(n);
                }
            }

            rooms.push({
                id: nextId++,
                tileRect: {
                    x: minX,
                    y: minY,
                    w: maxX - minX + 1,
                    h: maxY - minY + 1,
                },
                cells: Array.from({ length: maxY - minY + 1 }, (_, row) => {
                    const y = minY + row;
                    return Array.from({ length: maxX - minX + 1 }, (_, col) => (
                        collisionRows[y]?.[minX + col] === "."
                    ));
                }),
            });
        }
    }

    return rooms;
}

/**
 * Marker cells from an invisible layer (any non-zero tile).
 * Layer names: Checkpoints / Checkpoint / Spawn
 */
export function getMarkerCells(map, names) {
    const layer = getLayer(map, names);
    if (!layer) return [];

    const cells = [];
    for (let y = 0; y < layer.height; y++) {
        for (let x = 0; x < layer.width; x++) {
            if (getTileGid(layer, x, y) > 0) cells.push({ x, y });
        }
    }
    return cells;
}

/**
 * Room regions from a Regions layer: paint each room with tiles (same or different).
 * Connected non-zero cells form one room (4-way flood fill).
 * Layer names: Regions / RoomRegions / Room_Regions
 * Returns null if layer missing (caller should fall back).
 */
export function buildRoomRectsFromRegionLayer(map) {
    const layer = getLayer(map, ["regions", "roomregions", "room_regions"]);
    if (!layer) return null;

    const visited = Array.from({ length: layer.height }, () =>
        Array(layer.width).fill(false),
    );
    const rooms = [];
    let nextId = 1;

    const filled = (x, y) => getTileGid(layer, x, y) > 0;

    for (let y = 0; y < layer.height; y++) {
        for (let x = 0; x < layer.width; x++) {
            if (!filled(x, y) || visited[y][x]) continue;

            let minX = x;
            let maxX = x;
            let minY = y;
            let maxY = y;
            const stack = [{ x, y }];
            const cells = [];
            visited[y][x] = true;

            while (stack.length > 0) {
                const cell = stack.pop();
                cells.push(cell);
                minX = Math.min(minX, cell.x);
                maxX = Math.max(maxX, cell.x);
                minY = Math.min(minY, cell.y);
                maxY = Math.max(maxY, cell.y);

                for (const n of [
                    { x: cell.x + 1, y: cell.y },
                    { x: cell.x - 1, y: cell.y },
                    { x: cell.x, y: cell.y + 1 },
                    { x: cell.x, y: cell.y - 1 },
                ]) {
                    if (n.x < 0 || n.y < 0 || n.x >= layer.width || n.y >= layer.height) continue;
                    if (!filled(n.x, n.y) || visited[n.y][n.x]) continue;
                    visited[n.y][n.x] = true;
                    stack.push(n);
                }
            }

            const cellSet = new Set(cells.map((cell) => `${cell.x},${cell.y}`));
            rooms.push({
                id: nextId++,
                tileRect: {
                    x: minX,
                    y: minY,
                    w: maxX - minX + 1,
                    h: maxY - minY + 1,
                },
                cells: Array.from({ length: maxY - minY + 1 }, (_, row) => {
                    const cy = minY + row;
                    return Array.from({ length: maxX - minX + 1 }, (_, col) => {
                        const cx = minX + col;
                        return cellSet.has(`${cx},${cy}`);
                    });
                }),
            });
        }
    }

    return rooms;
}
